import "react-datepicker/dist/react-datepicker.css";
import { subDays, subYears } from "date-fns";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { Dropdown, Form, Message } from "semantic-ui-react";
import { Config } from "../../../../config/api";
import { getReadableClientName, getTimeRollUps } from "../../../../libs/common_utils";
import { walmartMXTranslations } from "../../../../translations/walmartMXTranslations";
import { AdvertiserField } from "../../common/components/AdvertiserField";
import { CampaignsField } from "../../common/components/CampaignsField";
import { DateRangePickerField } from "../../common/components/DateRangePickerField";
import { DateWindowField } from "../../common/components/DateWindowField";
import { useReportForm } from "../../hooks/useReportForm";

const DATE_NOW = new Date();
const MINIMAL_START_DATE = subYears(DATE_NOW, 1);
const MAXIMAL_END_DATE = subDays(DATE_NOW, 1);
// Audience report min date should be oct 1st 2025: https://infillion.atlassian.net/browse/EBU-307
const AUDIENCE_REPORT_MIN_DATE = new Date(2025, 9, 1);

const ReportParamsForm = ({ values, updateValues, onSubmit, type, hasConversionColumns, hasStrategyColumns }) => {
  const intl = useIntl();
  const timeRollup = getTimeRollUps(intl);
  const {
    advertisers,
    campaigns,
    advertisersLoading,
    campaignsLoading,
    campaignSearchQuery,
    handleStartDateChange,
    handleEndDateChange,
    resetSelectedCampaigns,
    setCampaignSearchQuery,
    trimmingSearch,
    performSearchAdvertisersDebounced,
    performSearchCampaignsDebounced,
    setAdvertisers,
    initialAdvertisers,
    resetCampaigns,
  } = useReportForm({ values, updateValues, type });
  const isOfflineAttributionReport = type === "omnichannel_attribution";
  const isAudienceReport = type === "audience";
  const isRetailAttributionReport = type === "retail_attribution";
  const isWmx = getReadableClientName(Config.public_client).toLowerCase() === "walmart";
  let tooltipMessage;

  if (isOfflineAttributionReport) {
    tooltipMessage = isWmx
      ? walmartMXTranslations.tooltipOfflineAttributionSpanish
      : intl.formatMessage({
          id: "TOOLTIP_OFFLINE_ATTRIBUTION",
          defaultMessage: "Note: Please note that offline data updates may be delayed by up to 72 hours.",
        });
  }

  if (isAudienceReport) {
    tooltipMessage = isWmx
      ? walmartMXTranslations.tooltipAudienceReportSpanish
      : intl.formatMessage({
          id: "TOOLTIP_AUDIENCE_REPORT",
          defaultMessage: "Note: Please note that audience data updates may be delayed by up to 72 hours.",
        });
  }

  if (isRetailAttributionReport) {
    tooltipMessage = isWmx
      ? walmartMXTranslations.tooltipRetailAttributionSpanish
      : intl.formatMessage({
          id: "TOOLTIP_RETAIL_ATTRIBUTION",
          defaultMessage: "Note: Please be aware that offline data updates may take up to 72 hours to update.",
        });
  }

  return (
    <Form
      autoComplete="off"
      onSubmit={onSubmit}
    >
      {isRetailAttributionReport &&
        hasConversionColumns &&
        (() => {
          const noteText = isWmx
            ? walmartMXTranslations.retailAttributionConversionsNote
            : intl.formatMessage({
                id: "NOTE_RETAIL_ATTRIBUTION_CONVERSIONS",
                defaultMessage:
                  "Important: When selecting the conversions dimension, consider the following implications before running your analysis. At the campaign level, a conversion represents a transaction that includes at least one of the selected brands or UPCs. In the Retail Attribution report, conversions can be attributed to multiple selected brands in the ROI pixel, so the total conversions per brand may exceed the total conversions for the campaign.",
              });
          const colonIdx = noteText.indexOf(":");
          const notePrefix = colonIdx >= 0 ? noteText.slice(0, colonIdx) : "";
          const noteBody = colonIdx >= 0 ? noteText.slice(colonIdx + 1).trimStart() : noteText;
          return (
            <Message
              size="tiny"
              style={{
                marginTop: "10px",
                backgroundColor: "#f7f8fa",
                color: "#1a1a1a",
                boxShadow: "inset 3px 0 0 0 var(--header-menu-background)",
                borderRadius: "4px",
                border: "1px solid #dde0e8",
              }}
            >
              <span
                style={{ color: "#db2828", marginRight: "4px" }}
                aria-hidden="true"
              >
                *
              </span>
              <strong>{notePrefix}:</strong> {noteBody}
            </Message>
          );
        })()}
      {isRetailAttributionReport &&
        hasStrategyColumns &&
        (() => {
          const noteText = isWmx
            ? walmartMXTranslations.retailAttributionStrategyNote
            : intl.formatMessage({
                id: "NOTE_RETAIL_ATTRIBUTION_STRATEGY",
                defaultMessage:
                  "Important: The breakdown by Brand and/or UPC applies only at the Campaign level. If you select Strategy, you cannot combine it with Brand and UPC.",
              });
          const colonIdx = noteText.indexOf(":");
          const notePrefix = colonIdx >= 0 ? noteText.slice(0, colonIdx) : "";
          const noteBody = colonIdx >= 0 ? noteText.slice(colonIdx + 1).trimStart() : noteText;
          return (
            <Message
              size="tiny"
              style={{
                marginTop: "10px",
                backgroundColor: "#f7f8fa",
                color: "#1a1a1a",
                boxShadow: "inset 3px 0 0 0 var(--header-menu-background)",
                borderRadius: "4px",
                border: "1px solid #dde0e8",
              }}
            >
              <span
                style={{ color: "#db2828", marginRight: "4px" }}
                aria-hidden="true"
              >
                *
              </span>
              <strong>{notePrefix}:</strong> {noteBody}
            </Message>
          );
        })()}
      <Form.Group>
        <DateWindowField
          values={values}
          updateValues={updateValues}
          dateWindowType="reporting_period"
          tooltipMessage={tooltipMessage}
        />

        {values.reporting_period === "custom" && (
          <DateRangePickerField
            values={values}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            minimalStartDate={isAudienceReport ? AUDIENCE_REPORT_MIN_DATE : MINIMAL_START_DATE}
            maximalEndDate={MAXIMAL_END_DATE}
          />
        )}

        <Form.Field className="filter-field">
          <label>
            {intl.formatMessage({
              id: "LABEL_AGGREGATION",
              defaultMessage: "Aggregation",
            })}
          </label>
          <Dropdown
            data-id="time_rollup"
            data-testid="report-aggregation-dropdown"
            selection
            options={timeRollup}
            name="time_rollup"
            value={values.time_rollup}
            selectOnNavigation={false}
            onChange={(_e, data) => {
              updateValues({
                // biome-ignore lint/style/useNamingConvention: <server prop>
                time_rollup: data.value,
              });
            }}
          />
        </Form.Field>

        <AdvertiserField
          values={values}
          updateValues={updateValues}
          advertisers={advertisers}
          advertisersLoading={advertisersLoading}
          campaignsLoading={campaignsLoading}
          trimmingSearch={trimmingSearch}
          performSearchAdvertisersDebounced={performSearchAdvertisersDebounced}
          setAdvertisers={setAdvertisers}
          initialAdvertisers={initialAdvertisers}
        />

        <CampaignsField
          values={values}
          updateValues={updateValues}
          campaigns={campaigns}
          campaignsLoading={campaignsLoading}
          advertisersLoading={advertisersLoading}
          trimmingSearch={trimmingSearch}
          resetSelectedCampaigns={resetSelectedCampaigns}
          campaignSearchQuery={campaignSearchQuery}
          setCampaignSearchQuery={setCampaignSearchQuery}
          performSearchCampaignsDebounced={performSearchCampaignsDebounced}
          type={type}
          resetCampaigns={resetCampaigns}
        />
      </Form.Group>
    </Form>
  );
};
ReportParamsForm.propTypes = {
  values: PropTypes.object.isRequired,
  updateValues: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  hasConversionColumns: PropTypes.bool,
  hasStrategyColumns: PropTypes.bool,
};
ReportParamsForm.defaultProps = {
  hasConversionColumns: false,
  hasStrategyColumns: false,
};

export default ReportParamsForm;
